import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import styled from 'styled-components';
import { breakpoints } from '../helpers/breakpoints';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import ReactBnbGallery from 'react-bnb-gallery';

import Layout from '../components/layout';
import SEO from '../components/seo';
import Main from '../components/main';
import BackLink from '../components/back-link';
import StyledProperty from '../components/property';

const StyledPropertyHeader = styled.header`
  display: grid;
  grid-template-columns: 1fr;
  margin-bottom: 3rem;
  margin-left: auto;
  margin-right: auto;
  padding-right: var(--gutter);
  max-width: ${breakpoints.mw};
  @media screen and (min-width: ${breakpoints.lg}) {
    margin-bottom: 6rem;
  }
`;

const StyledPropertyHeaderImagery = styled.div`
  position: relative;
  width: 100%;
  height: 25rem;
  overflow: hidden;
  border-radius: 0 var(--border-radius) var(--border-radius) 0;
  @media screen and (min-width: ${breakpoints.md}) {
    height: 35rem;
  }
  @media screen and (min-width: ${breakpoints.lg}) {
    height: 50rem;
  }
`;

const ViewGalleryButton = styled.button`
  position: absolute;
  right: 2rem;
  bottom: 2rem;
  background: var(--c-white);
  z-index: 1;
  padding: 1.5rem 2rem;
  border-radius: var(--border-radius);
  border: none;
  font-size: 1.4rem;
  /* font-weight: 300; */
  transition: all 0.3s ease;
  cursor: pointer;
  @media (hover: hover) {
    &:hover {
      background: var(--c-blue);
      color: var(--c-white);
      transform: scale(1.05);
    }
  }
`;

const StyledPropertyWrapper = styled.section`
  display: grid;
  grid-gap: var(--gutter);
  width: 100%;
  margin: var(--v-spacing) auto;
  @media screen and (min-width: ${breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }
  @media screen and (min-width: ${breakpoints.xl}) {
    width: 80%;
  }
`;

const PropertyPage = PropertyPageQuery => {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const { contentfulProperty: property } = PropertyPageQuery.data;
  const { festival } = PropertyPageQuery.pageContext;
  const { homes, images } = property;
  const galleryImages = images.map(image => {
    return {
      photo: image.fixed.src,
    };
  });
  const description = property.childContentfulPropertyDescriptionRichTextNode.json;
  const ogSocialImage = images[0].fixed.src.slice(2);
  return (
    <Layout>
      <SEO title={`${property.name} | ${festival.name}`} ogImage={ogSocialImage} />
      <StyledPropertyHeader>
        <StyledPropertyHeaderImagery>
          <Img
            fluid={images[0].fluid}
            alt={`Interior shot of ${property.name}`}
            style={{
              height: '100%',
            }}
          />
          <ViewGalleryButton onClick={() => setGalleryOpen(!galleryOpen)}>View Gallery</ViewGalleryButton>
        </StyledPropertyHeaderImagery>
        <ReactBnbGallery show={galleryOpen} photos={galleryImages} onClose={() => setGalleryOpen(!galleryOpen)} />
      </StyledPropertyHeader>
      <Main>
        <BackLink to={`/festivals/${festival.url}/properties`} text={`View all ${festival.name} properties`} />
        <h1>{property.name}</h1>
        {documentToReactComponents(description)}
        <StyledPropertyWrapper>
          {homes
            ? homes.map(home => (
                <StyledProperty
                  key={home.name}
                  name={home.name}
                  images={home.images}
                  sleeps={home.sleeps}
                  price={home.price}
                  description={home.childContentfulHomeDescriptionRichTextNode.json}
                />
              ))
            : null}
        </StyledPropertyWrapper>
      </Main>
    </Layout>
  );
};

export default PropertyPage;

export const PropertyPageQuery = graphql`
  query($url: String!) {
    contentfulProperty(url: { eq: $url }) {
      name
      images {
        fluid(maxWidth: 1680, quality: 100) {
          ...GatsbyContentfulFluid_withWebp
        }
        fixed(width: 1200, height: 630) {
          src
        }
      }
      childContentfulPropertyDescriptionRichTextNode {
        json
      }
      homes {
        name
        price
        sleeps
        images {
          fluid(maxWidth: 728, maxHeight: 400, quality: 100) {
            ...GatsbyContentfulFluid_withWebp
          }
          fixed(width: 1200, height: 630) {
            src
          }
        }
        childContentfulHomeDescriptionRichTextNode {
          json
        }
      }
    }
  }
`;
